import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/auth/AuthContext";

const inputCls = "bg-[#0a0a0c] border border-zinc-700 rounded px-3 py-2 text-xs text-white font-mono focus:border-[#d4af37] outline-none";

export function AdminGallery() {
  const { authHeaders } = useAuth();
  const [items, setItems] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => axios.get(`${API}/gallery`).then((r) => setItems(r.data)).catch(() => null);
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      await axios.post(`${API}/admin/gallery`, fd, { headers: authHeaders() });
      setCaption("");
      toast.success("Photo added to your gallery.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/admin/gallery/${id}`, { headers: authHeaders() });
    setItems((p) => p.filter((i) => i.id !== id));
    toast.success("Removed.");
  };

  return (
    <div data-testid="gallery-panel">
      <p className="text-[10px] font-mono text-zinc-500 mb-4">Photos you upload here replace the stock images in the homepage installation gallery.</p>
      <div className="flex flex-wrap gap-2 mb-6">
        <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional)" data-testid="gallery-caption" className={`${inputCls} flex-1 min-w-[180px]`} />
        <label className="px-5 py-2 rounded bg-[#d4af37] text-black text-[10px] font-mono font-bold uppercase tracking-wider hover:bg-[#e6ca65] transition-colors cursor-pointer" data-testid="gallery-upload">
          {uploading ? "Uploading..." : "Upload Photo"}
          <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => upload(e.target.files?.[0])} />
        </label>
      </div>
      {items === null ? <p className="text-zinc-500 font-mono text-xs">Loading...</p>
        : items.length === 0 ? <p className="text-zinc-500 font-mono text-xs py-6 text-center" data-testid="gallery-empty">No photos yet — the homepage shows the default gallery.</p>
        : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {items.map((i) => (
              <div key={i.id} className="rounded-lg overflow-hidden border border-zinc-800 relative group" data-testid={`gallery-item-${i.id}`}>
                <img src={i.link} alt={i.caption || "Installation photo"} className="w-full aspect-[4/3] object-cover" />
                <div className="absolute inset-x-0 bottom-0 bg-black/80 px-2 py-1.5 flex items-center justify-between gap-2">
                  <span className="text-[9px] font-mono text-zinc-300 truncate">{i.caption || i.id.slice(0, 8)}</span>
                  <button type="button" onClick={() => remove(i.id)} data-testid={`gallery-delete-${i.id}`}
                    className="text-[9px] font-mono text-red-400 hover:text-red-300">Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

export function AdminSettings() {
  const { authHeaders } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cal, setCal] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/settings`, { headers: authHeaders() }).then((r) => setS(r.data)).catch(() => null);
    axios.get(`${API}/admin/calendar/status`, { headers: authHeaders() }).then((r) => setCal(r.data)).catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectCalendar = async () => {
    try {
      const { data } = await axios.get(`${API}/oauth/google/login`, { headers: authHeaders() });
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start Google connection.");
    }
  };

  const disconnectCalendar = async () => {
    if (!window.confirm("Disconnect Google Calendar? New confirmations will no longer sync.")) return;
    await axios.post(`${API}/admin/calendar/disconnect`, {}, { headers: authHeaders() });
    setCal((p) => ({ ...p, connected: false, email: "" }));
    toast.success("Google Calendar disconnected.");
  };

  const save = async (patch, label = "Saved.") => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/settings`, patch, { headers: authHeaders() });
      setS(data);
      toast.success(label);
    } catch { toast.error("Save failed."); } finally { setSaving(false); }
  };

  if (!s) return <p className="text-zinc-500 font-mono text-xs">Loading...</p>;

  return (
    <div className="space-y-8" data-testid="settings-panel">
      <div className="rounded-xl bg-[#121216] border border-zinc-800 p-5" data-testid="calendar-card">
        <h3 className="font-display text-lg uppercase text-white mb-1">Google Calendar Sync</h3>
        <p className="text-[10px] font-mono text-zinc-500 mb-4">
          Confirmed appointments are added to your Google Calendar automatically. Only DuroTech28 bookings block a slot — your personal events never do.
        </p>
        {cal === null ? <p className="text-zinc-500 font-mono text-xs">Loading...</p>
          : !cal.configured ? (
            <p className="text-[11px] font-mono text-amber-400" data-testid="calendar-not-configured">Google credentials are not configured on the server yet.</p>
          ) : cal.connected ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[11px] font-mono text-emerald-400" data-testid="calendar-connected">Connected: {cal.email}</span>
              <button type="button" onClick={disconnectCalendar} data-testid="calendar-disconnect"
                className="px-4 py-2 rounded border border-red-500/40 text-[10px] font-mono uppercase tracking-wider text-red-400 hover:bg-red-500/10 transition-colors">
                Disconnect
              </button>
            </div>
          ) : (
            <button type="button" onClick={connectCalendar} data-testid="calendar-connect"
              className="px-5 py-2.5 rounded bg-[#d4af37] text-black text-[10px] font-mono font-bold uppercase tracking-wider hover:bg-[#e6ca65] transition-colors">
              Connect Google Calendar
            </button>
          )}
      </div>
      <div className="rounded-xl bg-[#121216] border border-zinc-800 p-5">
        <h3 className="font-display text-lg uppercase text-white mb-4">Equipment Deposits</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[["flat", "Flat Mount"], ["tilting", "Tilting Mount"], ["full_motion", "Full-Motion"], ["standard_large", '76–100" Standard']].map(([k, label]) => (
            <div key={k}>
              <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">{label}</span>
              <div className="flex items-center gap-1">
                <span className="text-[#d4af37] text-sm font-mono">$</span>
                <input type="number" value={s.deposits[k]}
                  onChange={(e) => setS((p) => ({ ...p, deposits: { ...p.deposits, [k]: Number(e.target.value) } }))}
                  data-testid={`deposit-${k}`} className={`${inputCls} w-24`} />
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => save({ deposits: s.deposits })} disabled={saving}
          data-testid="save-deposits" className="mt-4 px-5 py-2 rounded bg-[#d4af37] text-black text-[10px] font-mono font-bold uppercase tracking-wider hover:bg-[#e6ca65] transition-colors disabled:opacity-50">
          Save Deposits
        </button>
      </div>

      <div className="rounded-xl bg-[#121216] border border-zinc-800 p-5">
        <h3 className="font-display text-lg uppercase text-white mb-1">Cable Concealment Options</h3>
        <p className="text-[10px] font-mono text-zinc-500 mb-4">Shown during TV mounting booking. Custom is never auto-charged — its price is the displayed starting price.</p>
        <div className="space-y-4">
          {s.concealment_options.map((o, idx) => (
            <div key={o.key} className="rounded-lg border border-zinc-800 p-3" data-testid={`conceal-opt-${o.key}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs text-zinc-200 font-medium min-w-[180px]">{o.name} {o.kind === "custom" && <span className="text-amber-400 font-mono text-[9px]">· starting price</span>}</span>
                <div className="flex items-center gap-1">
                  <span className="text-[#d4af37] text-sm font-mono">$</span>
                  <input type="number" value={o.price}
                    onChange={(e) => setS((p) => ({ ...p, concealment_options: p.concealment_options.map((x, i) => i === idx ? { ...x, price: Number(e.target.value) || 0 } : x) }))}
                    data-testid={`conceal-price-${o.key}`} className={`${inputCls} w-24`} />
                </div>
              </div>
              <textarea rows="2" value={o.desc}
                onChange={(e) => setS((p) => ({ ...p, concealment_options: p.concealment_options.map((x, i) => i === idx ? { ...x, desc: e.target.value } : x) }))}
                data-testid={`conceal-desc-${o.key}`} className={`${inputCls} w-full mt-2`}></textarea>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => save({ concealment_options: s.concealment_options })} disabled={saving}
          data-testid="save-concealment" className="mt-4 px-5 py-2 rounded bg-[#d4af37] text-black text-[10px] font-mono font-bold uppercase tracking-wider hover:bg-[#e6ca65] transition-colors disabled:opacity-50">
          Save Concealment Options
        </button>
      </div>

      <div className="rounded-xl bg-[#121216] border border-zinc-800 p-5">
        <h3 className="font-display text-lg uppercase text-white mb-1">Featured TV Mounting Card</h3>
        <p className="text-[10px] font-mono text-zinc-500 mb-4">The highlighted card at the top of the homepage services section. Prices pull from your TV package matrix.</p>
        <label className="flex items-center gap-2 text-[11px] font-mono text-zinc-300 mb-4">
          <input type="checkbox" checked={s.featured_service.visible}
            onChange={(e) => setS((p) => ({ ...p, featured_service: { ...p.featured_service, visible: e.target.checked } }))}
            data-testid="featured-visible" className="accent-[#d4af37]" />
          Show featured card on homepage
        </label>
        <div className="grid sm:grid-cols-2 gap-3">
          {[["headline", "Headline"], ["subheadline", "Subheadline"], ["tagline", "Tagline"]].map(([k, label]) => (
            <div key={k}>
              <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">{label}</span>
              <input value={s.featured_service[k]}
                onChange={(e) => setS((p) => ({ ...p, featured_service: { ...p.featured_service, [k]: e.target.value } }))}
                data-testid={`featured-${k}`} className={`${inputCls} w-full`} />
            </div>
          ))}
          <div className="sm:col-span-2">
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Description</span>
            <textarea rows="2" value={s.featured_service.description}
              onChange={(e) => setS((p) => ({ ...p, featured_service: { ...p.featured_service, description: e.target.value } }))}
              data-testid="featured-description" className={`${inputCls} w-full`}></textarea>
          </div>
        </div>
        <button type="button" onClick={() => save({ featured_service: s.featured_service })} disabled={saving}
          data-testid="save-featured" className="mt-4 px-5 py-2 rounded bg-[#d4af37] text-black text-[10px] font-mono font-bold uppercase tracking-wider hover:bg-[#e6ca65] transition-colors disabled:opacity-50">
          Save Featured Card
        </button>
      </div>

      <div className="rounded-xl bg-[#121216] border border-zinc-800 p-5">
        <h3 className="font-display text-lg uppercase text-white mb-2">Cancellation &amp; Refund Policy</h3>
        <p className="text-[10px] font-mono text-zinc-500 mb-3">Shown to customers at booking review.</p>
        <textarea rows="3" value={s.cancellation_policy}
          onChange={(e) => setS((p) => ({ ...p, cancellation_policy: e.target.value }))}
          data-testid="policy-input" className={`${inputCls} w-full`}></textarea>
        <button type="button" onClick={() => save({ cancellation_policy: s.cancellation_policy })} disabled={saving}
          data-testid="save-policy" className="mt-3 px-5 py-2 rounded bg-[#d4af37] text-black text-[10px] font-mono font-bold uppercase tracking-wider hover:bg-[#e6ca65] transition-colors disabled:opacity-50">
          Save Policy
        </button>
      </div>
    </div>
  );
}


export function AdminPackages() {
  const { authHeaders } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/settings`, { headers: authHeaders() }).then((r) => setS(r.data)).catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!s) return null;

  const setPrice = (key, pkg, v) =>
    setS((p) => ({
      ...p,
      tv_packages: p.tv_packages.map((t) =>
        t.key === key ? { ...t, prices: { ...t.prices, [pkg]: Number(v) || 0 } } : t),
    }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/settings`, { tv_packages: s.tv_packages }, { headers: authHeaders() });
      setS(data);
      toast.success("TV packages saved.");
    } catch { toast.error("Save failed."); } finally { setSaving(false); }
  };

  const PKG_LABELS = [["install", "Install Only"], ["flat", "Flat"], ["tilting", "Tilting"], ["full_motion", "Full-Motion"]];

  return (
    <div className="rounded-xl bg-[#121216] border border-[#d4af37]/30 p-5 mb-8" data-testid="packages-panel">
      <h3 className="font-display text-lg uppercase text-white mb-1">TV Mount Packages</h3>
      <p className="text-[10px] font-mono text-zinc-500 mb-4">Package prices by TV size. 76"+ sizes include the heavy-duty flat mount — flat equals standard there.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead>
            <tr className="text-left text-zinc-500 text-[9px] uppercase tracking-widest">
              <th className="pb-2 pr-3">TV Size</th>
              {PKG_LABELS.map(([k, l]) => <th key={k} className="pb-2 pr-3">{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {s.tv_packages.map((t) => (
              <tr key={t.key} className="border-t border-zinc-800" data-testid={`pkg-row-${t.key}`}>
                <td className="py-2 pr-3 text-zinc-300 whitespace-nowrap">{t.label}</td>
                {PKG_LABELS.map(([k]) => (
                  <td key={k} className="py-2 pr-3">
                    <div className="flex items-center gap-1">
                      <span className="text-[#d4af37]">$</span>
                      <input type="number" value={t.prices[k]}
                        onChange={(e) => setPrice(t.key, k, e.target.value)}
                        data-testid={`pkg-price-${t.key}-${k}`}
                        className="bg-[#0a0a0c] border border-zinc-700 rounded px-2 py-1.5 w-20 text-white focus:border-[#d4af37] outline-none" />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={save} disabled={saving} data-testid="save-packages"
        className="mt-4 px-5 py-2 rounded bg-[#d4af37] text-black text-[10px] font-mono font-bold uppercase tracking-wider hover:bg-[#e6ca65] transition-colors disabled:opacity-50">
        Save Packages
      </button>
    </div>
  );
}
